import axios, { AxiosError, AxiosResponse } from 'axios';
import config from '../../config';
import { AuthUser } from '../../redux/auth/types';

//accept, accept-encoding, authorization, content-type, dnt, origin, user-agent, x-csrftoken, x-requested-with

// content type
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.post['Accept'] = 'application/json';
axios.defaults.baseURL = config.API_URL;

// intercepting to capture errors
let hasRedirectedAccessDenied = false;
axios.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (error: AxiosError | unknown) => {
        let message: string;

        if (axios.isAxiosError(error)) {
            const axiosError: AxiosError = error;

            if (axiosError.message?.includes('Network Error')) {
                return Promise.reject('Erro na Rede');
            }

            if (axiosError.response?.status) {
                if (
                    axiosError.response?.data &&
                    (
                        (axiosError.response.data as any)?.non_field_errors?.[0]?.includes('Unable to log in with provided credentials.') ||
                        (typeof axiosError.response.data === 'string' && (axiosError.response.data as string).includes('DoesNotExist'))
                    )
                ) {
                    return Promise.reject('Não é possível logar com as credenciais informadas');
                }

                switch (axiosError.response.status) {
                    case 401:
                        return Promise.reject('Dados inválidos');
                    case 403:
                        if (!hasRedirectedAccessDenied) {
                            hasRedirectedAccessDenied = true;
                            window.location.replace('/access-denied');
                        }
                        return Promise.reject('Access Forbidden');
                    case 404:
                        return Promise.reject('Sorry! the data you are looking for could not be found');
                    default: {
                        const data: any = axiosError.response?.data;
                        const fallback = axiosError.message ?? axiosError.toString();
                        let msg: string = fallback;
                        if (data) {
                            if (typeof data === 'string') {
                                msg = data;
                            } else if (typeof data.message === 'string') {
                                msg = data.message;
                            } else if (typeof data === 'object') {
                                const first = Object.entries(data).find(([_, v]) => {
                                    return typeof v === 'string' || (Array.isArray(v) && typeof v[0] === 'string');
                                });
                                if (first) {
                                    const [field, v] = first;
                                    msg = `${field}: ${Array.isArray(v) ? v[0] : v}`;
                                }
                            }
                        }
                        return Promise.reject(msg);
                    }
                }
            }
        }

        const e: any = error as any;
        if (e?.response?.status === 404) {
            // window.location.href = '/not-found';
        } else if (e?.response?.status === 403) {
            if (!hasRedirectedAccessDenied) {
                hasRedirectedAccessDenied = true;
                window.location.replace('/access-denied');
            }
        } else {
            switch (e?.response?.status) {
                case 401:
                    message = 'Dados inválidos';
                    break;
                case 403:
                    message = 'Access Forbidden';
                    break;
                case 404:
                    message = 'Sorry! the data you are looking for could not be found';
                    break;
                default: {
                    const data: any = e?.response?.data;
                    const fallback = e?.message ?? String(e);
                    let msg: string = fallback;
                    if (data) {
                        if (typeof data === 'string') {
                            msg = data;
                        } else if (typeof data.message === 'string') {
                            msg = data.message;
                        } else if (typeof data === 'object') {
                            const first = Object.entries(data).find(([_, v]) => {
                                return typeof v === 'string' || (Array.isArray(v) && typeof v[0] === 'string');
                            });
                            if (first) {
                                const [field, v] = first;
                                msg = `${field}: ${Array.isArray(v) ? v[0] : v}`;
                            }
                        }
                    }
                    message = msg;
                }
            }
            return Promise.reject(message);
        }
    }
);

const AUTH_SESSION_KEY = 'hyper_user';

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
    if (token) axios.defaults.headers.common['Authorization'] = 'token ' + token;
    else delete axios.defaults.headers.common['Authorization'];
};

const getUserFromSession: () => AuthUser = () => {
    const user = sessionStorage.getItem(AUTH_SESSION_KEY);
    return user ? (typeof user == 'object' ? user : JSON.parse(user)) : null;
};
class APICore {
    /**
     * Fetches data from given url
     */
    get = (url: string, params?: any) => {
        let finalUrl = url;
        let finalParams = params;

        const path = finalUrl.split('?')[0];
        const hasPageQuery = finalUrl.includes('page=');
        const hasPageParam = !!(finalParams && Object.prototype.hasOwnProperty.call(finalParams, 'page'));

        // Endpoints que devem retornar lista "plana"
        const shouldFlat =
            // cursos (lista)
            (path.startsWith('/cursos/curso/') && !/^\/cursos\/curso\/[A-Za-z0-9-]+\/$/.test(path)) ||
            // turmas (lista)
            (path.startsWith('/turmas/turma/') && !/^\/turmas\/turma\/[A-Za-z0-9-]+\/$/.test(path)) ||
            // cursos do usuário (lista)
            path.startsWith('/cursos/curso-usuario/') ||
            // equipamentos (lista)
            path.startsWith('/equipamentos/equipamento/') ||
            // conteúdos (lista)
            path.startsWith('/conteudos/conteudo/') ||
            // histórico (lista)
            path.startsWith('/historico/get-ultimo-estado-super/') ||
            // usuários (somente quando não há paginação)
            (path.startsWith('/usuarios/usuario/') && !hasPageQuery && !hasPageParam);

        if (shouldFlat) {
            if (finalParams) {
                finalParams = { ...finalParams, flat: true };
            } else if (finalUrl.includes('?')) {
                finalUrl = `${finalUrl}&flat=true`;
            } else {
                finalUrl = `${finalUrl}?flat=true`;
            }
        }

        if (finalParams) {
            const queryString = Object.keys(finalParams)
                .map((key) => key + '=' + finalParams[key])
                .join('&');
            const separator = finalUrl.includes('?') ? '&' : '?';
            return axios.get(`${finalUrl}${separator}${queryString}`, finalParams);
        } else {
            return axios.get(`${finalUrl}`, finalParams);
        }
    };

    getFile = (url: string, params: any) => {
        let finalUrl = url;
        let finalParams = params;

        const path = finalUrl.split('?')[0];

        const shouldFlat =
            (path.startsWith('/cursos/curso/') && !/^\/cursos\/curso\/[A-Za-z0-9-]+\/$/.test(path)) ||
            (path.startsWith('/turmas/turma/') && !/^\/turmas\/turma\/[A-Za-z0-9-]+\/$/.test(path)) ||
            path.startsWith('/cursos/curso-usuario/') ||
            path.startsWith('/equipamentos/equipamento/') ||
            path.startsWith('/conteudos/conteudo/') ||
            path.startsWith('/historico/get-ultimo-estado-super/') ||
            path.startsWith('/conteudos/download') ||
            (path.startsWith('/usuarios/usuario/') && !(finalParams && Object.prototype.hasOwnProperty.call(finalParams, 'page')));

        if (shouldFlat) {
            if (finalParams) {
                finalParams = { ...finalParams, flat: true };
            } else if (finalUrl.includes('?')) {
                finalUrl = `${finalUrl}&flat=true`;
            } else {
                finalUrl = `${finalUrl}?flat=true`;
            }
        }

        if (finalParams) {
            const queryString = Object.keys(finalParams)
                .map((key) => key + '=' + finalParams[key])
                .join('&');
            const separator = finalUrl.includes('?') ? '&' : '?';
            return axios.get(`${finalUrl}${separator}${queryString}`, { responseType: 'blob' });
        } else {
            return axios.get(`${finalUrl}`, { responseType: 'blob' });
        }
    };

    getMultiple = (urls: string, params: any) => {
        const reqs = [];
        let queryString = '';
        if (params) {
            queryString = params
                ? Object.keys(params)
                      .map((key) => key + '=' + params[key])
                      .join('&')
                : '';
        }

        for (const url of urls) {
            reqs.push(axios.get(`${url}?${queryString}`));
        }
        return axios.all(reqs);
    };

    /**
     * post given data to url
     */
    create = (url: string, data: any, headers: any = {}) => {
        if (headers?.Authorization) {
            axios.defaults.headers.common['Authorization'] = headers?.Authorization;
        }

        return axios.post(url, data, headers);
    };

    /**
     * Updates patch data
     */
    updatePatch = (url: string, data: any) => {
        return axios.patch(url, data);
    };

    /**
     * Updates data
     */
    update = (url: string, data: any) => {
        return axios.put(url, data);
    };

    /**
     * Deletes data
     */
    delete = (url: string) => {
        return axios.delete(url);
    };

    /**
     * post given data to url with file
     */
    createWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config: any = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post(url, formData, config);
    };

    /**
     * post given data to url with file
     */
    updateWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config: any = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.patch(url, formData, config);
    };

    isUserAuthenticated = () => {
        const user: AuthUser = this.getLoggedInUser();

        if (!user || !user.token || !user.validate) {
            return false;
        }

        const expMs = new Date(user.validate).getTime();
        if (!expMs || Number.isNaN(expMs)) {
            return false;
        }

        const nowMs = Date.now();
        return expMs > nowMs;
    };

    setLoggedInUser = (session: AuthUser | null | undefined) => {
        if (session) sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
        else {
            sessionStorage.removeItem(AUTH_SESSION_KEY);
        }
    };
    /**
     * Returns the logged in user
     */
    getLoggedInUser = () => {
        return getUserFromSession();
    };

    setUserInSession = (modifiedUser: any) => {
        let userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
        if (userInfo) {
            const { token, user } = JSON.parse(userInfo);
            this.setLoggedInUser({ token, ...user, ...modifiedUser });
        }
    };
}

/*
Check if token available in session
*/
let user = getUserFromSession();
if (user) {
    const { token } = user;
    if (token) {
        setAuthorization(token);
    }
}

export { APICore, setAuthorization };
